import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Plus,
  Trash2,
  Edit2,
  Users,
  Layers,
  Shield,
  User as UserIcon,
  Loader2,
  ChevronRight,
  ChevronDown,
  UserPlus,
  X,
  Mail,
  RefreshCw,
  Copy,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import {
  getWorkspaces,
  createWorkspace,
  updateWorkspace,
  deleteWorkspace,
  getWorkspaceMembers,
  addWorkspaceMember,
  updateWorkspaceMember,
  removeWorkspaceMember,
  getWorkspaceInvites,
  cancelWorkspaceInvite,
  resendWorkspaceInvite,
  getPlanFeatures,
} from '../services/api';
import { useWorkspace } from '../context/WorkspaceContext';
import PlanFeatures from '../components/admin/PlanFeatures';

// A workspace's per-feature setting: 'plan' (follow its plan), 'on' or 'off'.
const overrideValue = (ws, key) => {
  const v = ws?.features?.[key];
  return v === true ? 'on' : v === false ? 'off' : 'plan';
};
const overridePayload = { plan: null, on: true, off: false };

const copyInviteLink = (invite) => {
  const link = `${window.location.origin}/invite/${invite.token}`;
  navigator.clipboard.writeText(link)
    .then(() => toast.success('Invite link copied'))
    .catch(() => toast.error('Could not copy. Link: ' + link));
};

export default function WorkspaceManagement() {
  const { refreshWorkspaces } = useWorkspace();

  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedWs, setExpandedWs] = useState(null);
  const [membersMap, setMembersMap] = useState({});
  const [invitesMap, setInvitesMap] = useState({});
  const [membersLoading, setMembersLoading] = useState({});

  // Workspace dialogs
  const [showCreateWs, setShowCreateWs] = useState(false);
  const [showEditWs, setShowEditWs] = useState(false);
  const [showDeleteWs, setShowDeleteWs] = useState(false);
  const [selectedWs, setSelectedWs] = useState(null);
  const [wsForm, setWsForm] = useState({ name: '', description: '', plan: 'free', features: {} });
  const [planFeatures, setPlanFeatures] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Member dialogs
  const [showAddMember, setShowAddMember] = useState(null); // workspaceId
  const [showEditMember, setShowEditMember] = useState(null); // {workspaceId, member}
  const [showRemoveMember, setShowRemoveMember] = useState(null); // {workspaceId, member}
  const [memberForm, setMemberForm] = useState({ email: '', role: 'member' });
  const [memberRoleForm, setMemberRoleForm] = useState('member');

  const fetchWorkspaces = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getWorkspaces();
      setWorkspaces(data.workspaces || []);
    } catch {
      toast.error('Failed to load workspaces');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWorkspaces();
  }, [fetchWorkspaces]);

  useEffect(() => {
    getPlanFeatures().then(setPlanFeatures).catch(() => toast.error('Failed to load plan features'));
  }, []);

  const planIncludes = (plan, key) => !!planFeatures?.planFeatures?.[plan]?.[key];
  const featureOn = (ws, key) => {
    const o = overrideValue(ws, key);
    return o === 'plan' ? planIncludes(ws.plan || 'free', key) : o === 'on';
  };

  const fetchMembers = useCallback(async (wsId) => {
    setMembersLoading(prev => ({ ...prev, [wsId]: true }));
    try {
      const [membersData, invitesData] = await Promise.all([
        getWorkspaceMembers(wsId),
        getWorkspaceInvites(wsId),
      ]);
      setMembersMap(prev => ({ ...prev, [wsId]: membersData.members || [] }));
      setInvitesMap(prev => ({ ...prev, [wsId]: invitesData.invites || [] }));
    } catch {
      toast.error('Failed to load members');
    } finally {
      setMembersLoading(prev => ({ ...prev, [wsId]: false }));
    }
  }, []);

  const toggleExpand = (wsId) => {
    if (expandedWs === wsId) {
      setExpandedWs(null);
    } else {
      setExpandedWs(wsId);
      fetchMembers(wsId);
    }
  };

  // --- Workspace CRUD ---
  const handleCreateWs = async () => {
    if (!wsForm.name.trim()) {
      toast.error('Workspace name is required');
      return;
    }
    setSubmitting(true);
    try {
      await createWorkspace({ name: wsForm.name, description: wsForm.description });
      toast.success('Workspace created');
      setShowCreateWs(false);
      setWsForm({ name: '', description: '', plan: 'free', features: {} });
      fetchWorkspaces();
      refreshWorkspaces();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create workspace');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditWs = async () => {
    if (!wsForm.name.trim()) {
      toast.error('Workspace name is required');
      return;
    }
    setSubmitting(true);
    try {
      const features = {};
      (planFeatures?.features || []).forEach(({ key }) => {
        const next = wsForm.features?.[key] || 'plan';
        if (next !== overrideValue(selectedWs, key)) features[key] = overridePayload[next];
      });
      await updateWorkspace(selectedWs.id, {
        name: wsForm.name, description: wsForm.description, plan: wsForm.plan,
        ...(Object.keys(features).length ? { features } : {}),
      });
      toast.success('Workspace updated');
      setShowEditWs(false);
      fetchWorkspaces();
      refreshWorkspaces();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update workspace');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteWs = async () => {
    setSubmitting(true);
    try {
      await deleteWorkspace(selectedWs.id);
      toast.success('Workspace deleted');
      setShowDeleteWs(false);
      if (expandedWs === selectedWs.id) setExpandedWs(null);
      fetchWorkspaces();
      refreshWorkspaces();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete workspace');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditWs = (ws) => {
    setSelectedWs(ws);
    const features = {};
    (planFeatures?.features || []).forEach(({ key }) => { features[key] = overrideValue(ws, key); });
    setWsForm({ name: ws.name, description: ws.description || '', plan: ws.plan || 'free', features });
    setShowEditWs(true);
  };

  const openDeleteWs = (ws) => {
    setSelectedWs(ws);
    setShowDeleteWs(true);
  };

  // --- Member management ---
  const handleAddMember = async () => {
    if (!memberForm.email.trim()) {
      toast.error('Email is required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await addWorkspaceMember(showAddMember, { email: memberForm.email, role: memberForm.role });
      toast[res.emailSent === false ? 'warning' : 'success'](res.message || 'Invitation sent');
      setShowAddMember(null);
      setMemberForm({ email: '', role: 'member' });
      fetchMembers(showAddMember);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : (detail || 'Failed to add member');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateMemberRole = async () => {
    const { workspaceId, member } = showEditMember;
    setSubmitting(true);
    try {
      await updateWorkspaceMember(workspaceId, member.id, { role: memberRoleForm });
      toast.success('Role updated');
      setShowEditMember(null);
      fetchMembers(workspaceId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveMember = async () => {
    const { workspaceId, member } = showRemoveMember;
    setSubmitting(true);
    try {
      await removeWorkspaceMember(workspaceId, member.id);
      toast.success('Member removed');
      setShowRemoveMember(null);
      fetchMembers(workspaceId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove member');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelInvite = async (wsId, invite) => {
    try {
      await cancelWorkspaceInvite(wsId, invite.id);
      toast.success('Invite cancelled');
      fetchMembers(wsId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel invite');
    }
  };

  const handleResendInvite = async (wsId, invite) => {
    try {
      await resendWorkspaceInvite(wsId, invite.id);
      toast.success('Invite resent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend invite');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-zinc-100">Workspaces</h1>
          <p className="text-zinc-400 mt-1">Manage workspaces and their members</p>
        </div>
        <Button
          onClick={() => { setWsForm({ name: '', description: '', plan: 'free', features: {} }); setShowCreateWs(true); }}
          className="bg-emerald-600 hover:bg-emerald-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Workspace
        </Button>
      </div>

      <PlanFeatures data={planFeatures} onSaved={setPlanFeatures} />

      {/* Workspaces List */}
      <div className="space-y-3">
        {workspaces.length === 0 ? (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="py-12 text-center">
              <Layers className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-zinc-300 mb-2">No workspaces</h3>
              <p className="text-zinc-500 mb-4">Create your first workspace to get started</p>
              <Button
                onClick={() => { setWsForm({ name: '', description: '', plan: 'free', features: {} }); setShowCreateWs(true); }}
                variant="outline"
                className="border-zinc-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Workspace
              </Button>
            </CardContent>
          </Card>
        ) : (
          workspaces.map((ws) => {
            const isExpanded = expandedWs === ws.id;
            const members = membersMap[ws.id] || [];
            const invites = invitesMap[ws.id] || [];
            const isMembersLoading = membersLoading[ws.id];

            return (
              <Card key={ws.id} className="bg-zinc-900 border-zinc-800">
                {/* Workspace Row */}
                <CardContent className="p-4 sm:p-5">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => toggleExpand(ws.id)}
                      className="flex items-center gap-3 flex-1 min-w-0 text-left"
                    >
                      <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                        <Layers className="w-4 h-4 text-emerald-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-medium text-zinc-100">{ws.name}</h3>
                          <Badge
                            data-testid={`ws-plan-${ws.id}`}
                            className={ws.plan === 'pro'
                              ? 'text-xs bg-[#00E05E]/10 text-[#00E05E] border-[#00E05E]/20'
                              : 'text-xs bg-zinc-800 text-zinc-400 border-zinc-700'}
                          >
                            {ws.plan === 'pro' ? 'Pro' : 'Free'}
                          </Badge>
                          {(planFeatures?.features || []).map(({ key, label }) => (
                            <Badge
                              key={key}
                              data-testid={`ws-feature-${key}-${ws.id}`}
                              title={overrideValue(ws, key) === 'plan' ? 'Follows the plan' : 'Set for this workspace'}
                              className={featureOn(ws, key)
                                ? 'text-xs bg-sky-500/10 text-sky-300 border-sky-500/20'
                                : 'text-xs bg-zinc-800 text-zinc-500 border-zinc-700 line-through'}
                            >
                              {label}{overrideValue(ws, key) !== 'plan' ? ' *' : ''}
                            </Badge>
                          ))}
                          {ws.usage && (
                            <span className="text-xs text-zinc-500">
                              {ws.usage.channels}{ws.limits?.maxChannels != null ? `/${ws.limits.maxChannels}` : ''} channels
                            </span>
                          )}
                          {ws.memberCount !== undefined && (
                            <Badge variant="secondary" className="bg-zinc-800 text-zinc-400 text-xs">
                              <Users className="w-3 h-3 mr-1" />
                              {ws.memberCount}
                            </Badge>
                          )}
                        </div>
                        {ws.description && (
                          <p className="text-sm text-zinc-500 truncate mt-0.5">{ws.description}</p>
                        )}
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                      )}
                    </button>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <TooltipProvider delayDuration={300}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button variant="ghost" size="sm" onClick={() => openEditWs(ws)} className="text-zinc-400 hover:text-zinc-100 h-8 w-8 p-0" data-testid={`ws-edit-${ws.id}`}>
                              <Edit2 className="w-3.5 h-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Edit workspace</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button variant="ghost" size="sm" onClick={() => openDeleteWs(ws)} className="text-zinc-400 hover:text-red-400 h-8 w-8 p-0">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Delete workspace</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>

                  {/* Expanded Members Section */}
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t border-zinc-800">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-xs text-zinc-500 uppercase tracking-wider">Members</p>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setMemberForm({ email: '', role: 'member' }); setShowAddMember(ws.id); }}
                          className="h-7 text-xs border-zinc-700 text-zinc-300 hover:text-zinc-100"
                        >
                          <UserPlus className="w-3.5 h-3.5 mr-1.5" />
                          Add Member
                        </Button>
                      </div>

                      {isMembersLoading ? (
                        <div className="flex justify-center py-4">
                          <Loader2 className="w-5 h-5 text-emerald-500 animate-spin" />
                        </div>
                      ) : members.length === 0 && invites.length === 0 ? (
                        <p className="text-sm text-zinc-600 text-center py-4">No members yet</p>
                      ) : (
                        <div className="space-y-2">
                          {members.map((member) => (
                            <div
                              key={member.id}
                              className="flex items-center gap-3 px-3 py-2 rounded-lg bg-zinc-800/50"
                            >
                              <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                                member.workspaceRole === 'admin' ? 'bg-amber-500/10' : 'bg-zinc-700'
                              }`}>
                                {member.workspaceRole === 'admin' ? (
                                  <Shield className="w-3.5 h-3.5 text-amber-400" />
                                ) : (
                                  <UserIcon className="w-3.5 h-3.5 text-zinc-400" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-zinc-200 truncate">{member.name}</p>
                                <p className="text-xs text-zinc-500 truncate">{member.email}</p>
                              </div>
                              <Badge
                                className={`text-xs flex-shrink-0 ${
                                  member.workspaceRole === 'admin'
                                    ? 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                                    : 'bg-zinc-700 text-zinc-400 border-zinc-600'
                                }`}
                              >
                                {member.workspaceRole || 'member'}
                              </Badge>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <TooltipProvider delayDuration={300}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button variant="ghost" size="sm" onClick={() => { setMemberRoleForm(member.workspaceRole || 'member'); setShowEditMember({ workspaceId: ws.id, member }); }} className="text-zinc-500 hover:text-zinc-300 h-7 w-7 p-0">
                                        <Edit2 className="w-3 h-3" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Change role</TooltipContent>
                                  </Tooltip>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button variant="ghost" size="sm" onClick={() => setShowRemoveMember({ workspaceId: ws.id, member })} className="text-zinc-500 hover:text-red-400 h-7 w-7 p-0">
                                        <X className="w-3 h-3" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Remove from workspace</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                            </div>
                          ))}
                          {invites.map((invite) => (
                            <div
                              key={invite.id}
                              className="flex items-center gap-3 px-3 py-2 rounded-lg bg-zinc-800/30 border border-dashed border-zinc-700"
                            >
                              <div className="w-7 h-7 rounded-full bg-zinc-700/50 flex items-center justify-center flex-shrink-0">
                                <Mail className="w-3.5 h-3.5 text-zinc-500" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-zinc-500 truncate">{invite.email}</p>
                                <p className="text-xs text-zinc-600 capitalize">{invite.role}</p>
                              </div>
                              <Badge className="text-xs flex-shrink-0 bg-yellow-500/10 text-yellow-400 border-yellow-500/20">
                                Pending
                              </Badge>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <TooltipProvider delayDuration={300}>
                                  <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" onClick={() => copyInviteLink(invite)} className="text-zinc-500 hover:text-emerald-400 h-7 w-7 p-0" data-testid="copy-invite-link">
                            <Copy className="w-3 h-3" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Copy invite link</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button variant="ghost" size="sm" onClick={() => handleResendInvite(ws.id, invite)} className="text-zinc-500 hover:text-emerald-400 h-7 w-7 p-0">
                                        <RefreshCw className="w-3 h-3" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Resend invite</TooltipContent>
                                  </Tooltip>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button variant="ghost" size="sm" onClick={() => handleCancelInvite(ws.id, invite)} className="text-zinc-500 hover:text-red-400 h-7 w-7 p-0">
                                        <X className="w-3 h-3" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Cancel invite</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Create Workspace Dialog */}
      <Dialog open={showCreateWs} onOpenChange={setShowCreateWs}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Create Workspace</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Create a new workspace to organize channels and users
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Name *</Label>
              <Input
                value={wsForm.name}
                onChange={(e) => setWsForm({ ...wsForm, name: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-zinc-100"
                placeholder="My Workspace"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Description</Label>
              <Input
                value={wsForm.description}
                onChange={(e) => setWsForm({ ...wsForm, description: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-zinc-100"
                placeholder="Optional description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateWs(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={handleCreateWs} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Workspace Dialog */}
      <Dialog open={showEditWs} onOpenChange={setShowEditWs}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Edit Workspace</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Name *</Label>
              <Input
                value={wsForm.name}
                onChange={(e) => setWsForm({ ...wsForm, name: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-zinc-100"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Description</Label>
              <Input
                value={wsForm.description}
                onChange={(e) => setWsForm({ ...wsForm, description: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-zinc-100"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Plan</Label>
              <Select value={wsForm.plan} onValueChange={(v) => setWsForm({ ...wsForm, plan: v })}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-100" data-testid="ws-plan-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="free">Free — 2 channels</SelectItem>
                  <SelectItem value="pro">Pro — unlimited channels</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-500">Only platform admins can change plans. Existing channels are never removed on downgrade.</p>
            </div>
            {(planFeatures?.features || []).map(({ key, label }) => (
              <div className="space-y-2" key={key}>
                <Label className="text-zinc-300">{label}</Label>
                <Select
                  value={wsForm.features?.[key] || 'plan'}
                  onValueChange={(v) => setWsForm({ ...wsForm, features: { ...wsForm.features, [key]: v } })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-100" data-testid={`ws-feature-select-${key}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="plan">
                      Follow plan ({planIncludes(wsForm.plan, key) ? 'on' : 'off'} for {wsForm.plan === 'pro' ? 'Pro' : 'Free'})
                    </SelectItem>
                    <SelectItem value="on">On for this workspace</SelectItem>
                    <SelectItem value="off">Off for this workspace</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditWs(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={handleEditWs} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Workspace Dialog */}
      <Dialog open={showDeleteWs} onOpenChange={setShowDeleteWs}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Delete Workspace</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Are you sure you want to delete <span className="font-medium text-zinc-200">{selectedWs?.name}</span>?
              This will remove all associated channel assignments and memberships.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDeleteWs(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={handleDeleteWs} disabled={submitting} className="bg-red-600 hover:bg-red-700">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={!!showAddMember} onOpenChange={() => setShowAddMember(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Add Member</DialogTitle>
            <DialogDescription className="text-zinc-400">
              An invite will be sent by email. New users will be asked to create a password on first login.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Email *</Label>
              <Input
                type="email"
                value={memberForm.email}
                onChange={(e) => setMemberForm({ ...memberForm, email: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-zinc-100"
                placeholder="user@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Role</Label>
              <Select
                value={memberForm.role}
                onValueChange={(v) => setMemberForm({ ...memberForm, role: v })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-100">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddMember(null)} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={handleAddMember} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Member Role Dialog */}
      <Dialog open={!!showEditMember} onOpenChange={() => setShowEditMember(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Change Role</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Update role for <span className="font-medium text-zinc-200">{showEditMember?.member?.name}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={memberRoleForm} onValueChange={setMemberRoleForm}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-100">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="member">Member</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditMember(null)} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={handleUpdateMemberRole} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Member Dialog */}
      <Dialog open={!!showRemoveMember} onOpenChange={() => setShowRemoveMember(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Remove Member</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Remove <span className="font-medium text-zinc-200">{showRemoveMember?.member?.name}</span> from this workspace?
              Their account will not be deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowRemoveMember(null)} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={handleRemoveMember} disabled={submitting} className="bg-red-600 hover:bg-red-700">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
